import {
  Badge,
  BlockStack,
  Button,
  Card,
  Icon,
  InlineGrid,
  LegacyStack,
  Text,
} from '@shopify/polaris'
import { CheckIcon } from '@shopify/polaris-icons'
import React, { useState } from 'react'

import { LegacyHelpIcon } from '../common/HelpIcon'
import { ActivateDialog } from './ActivateDialog'

interface Props {
  name: string
  badge?: string
  isActive: boolean
  children: any
}

export const PricingPlan = (props: Props) => (
  <Card>
    <BlockStack gap="400">
      <LegacyStack distribution="equalSpacing" alignment="center">
        <Text as="span" tone="subdued">
          {props.name.toUpperCase()}
        </Text>
        {props.badge && (
          <Badge tone={props.isActive ? 'info' : 'attention'}>
            {props.isActive ? 'Current' : props.badge}
          </Badge>
        )}
      </LegacyStack>
      {props.children}
    </BlockStack>
  </Card>
)

const ActivateButton = ({
  id,
  defaultCap,
  fullWidth,
  disabled,
}: {
  id: string
  defaultCap: number
  fullWidth: boolean
  disabled: boolean
}) => {
  const [dialogVisible, setDialogVisible] = useState(false)

  return (
    <>
      <InlineGrid columns={{ xs: 1, sm: 1, md: 1, lg: fullWidth ? 1 : 2, xl: fullWidth ? 1 : 2 }}>
        <Button
          fullWidth
          onClick={() => setDialogVisible(true)}
          variant="primary"
          disabled={disabled}
        >
          Activate
        </Button>
      </InlineGrid>
      <ActivateDialog
        open={dialogVisible}
        onClose={() => setDialogVisible(false)}
        planId={id}
        defaultCap={defaultCap}
      />
    </>
  )
}

const Price = ({ period = 'month', children }: { period?: 'month' | null; children: any }) => (
  <LegacyStack spacing="tight" alignment="center">
    <Text variant="heading2xl" as="p">
      {children}
    </Text>
    {period && (
      <Text variant="headingLg" as="p">
        {' '}
        / {period}
      </Text>
    )}
  </LegacyStack>
)

const Fees = ({ children, helpText }: { children: any; helpText?: string }) => (
  <Text as="span" tone="subdued">
    {children}
    <LegacyHelpIcon>
      {helpText ??
        'A transaction fee applies to the entire Shopify order total, including shipping and taxes, when at least one subscription item is present. This includes both one-time and subscription products in the initial order. Transaction fees do not apply to Shopify orders containing only one-time products.'}
    </LegacyHelpIcon>
  </Text>
)

const Features = ({ featuresLabel, features }: { featuresLabel?: string; features: string[] }) => (
  <>
    <BlockStack gap="050">
      {featuresLabel && <Text as="strong">{featuresLabel}</Text>}
      {features.map((feature: string, index: number) => (
        <LegacyStack key={index} alignment="center" spacing="extraTight" wrap={false}>
          {feature && <Icon tone="success" source={CheckIcon} />}
          <p>{feature || <>&nbsp;</>}</p>
        </LegacyStack>
      ))}
      <br />
    </BlockStack>
  </>
)

PricingPlan.ActivateButton = ActivateButton
PricingPlan.Features = Features
PricingPlan.Fees = Fees
PricingPlan.Price = Price
